import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  FormControl,
  Button,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Box,
  Typography,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../utils/swalConfig';
import { useNavigate } from 'react-router-dom';

const ExternalCustomersPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  // API Functions
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://apidashboard.eurekagroup.id/external-customers', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      const customers = result.data || [];
      setData(customers);
      console.log('External customers loaded:', result);
    } catch (error) {
      console.error('Error fetching external customers:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal memuat data customer: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatBirthDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return 'success';
      case 0:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1:
        return 'Active';
      case 0:
        return 'Inactive';
      default:
        return 'Unknown';
    }
  };

  const filteredData = data
    .filter(customer => {
      const matchesSearch = customer.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                           customer.email?.toLowerCase().includes(searchText.toLowerCase()) ||
                           customer.phone?.toLowerCase().includes(searchText.toLowerCase());
      const matchesStatus = statusFilter === '' || customer.status?.toString() === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      const dateA = new Date(a.date_created || 0);
      const dateB = new Date(b.date_created || 0);
      return dateB - dateA;
    });

  const uniqueStatuses = [...new Set(data.map(customer => customer.status?.toString()))].filter(Boolean);

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <PeopleIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  External Customers
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola data customer eksternal dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="outlined" 
                color="primary" 
                startIcon={<RefreshIcon />} 
                onClick={fetchCustomers}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {loading ? <CircularProgress size={20} /> : 'Refresh'}
              </Button>
            </Box>
          }
          sx={{ pb: 2 }}
        />
        <Divider />
        <CardContent>
          {/* Filter Section */}
          <Box sx={{ 
            mb: 3, 
            p: 3, 
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200'
          }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cari Customer"
                  variant="outlined"
                  value={searchText}
                  onChange={handleSearchChange}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    displayEmpty
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterIcon color="action" />
                      </InputAdornment>
                    }
                    sx={{
                      borderRadius: 2,
                      bgcolor: (theme) => theme.palette.background.paper
                    }}
                  >
                    <MenuItem value="">
                      <em>Semua Status</em>
                    </MenuItem>
                    {uniqueStatuses.map(status => (
                      <MenuItem key={status} value={status}>
                        {status === '1' ? 'Active' : 'Inactive'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {filteredData.length}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Total Customer
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Table Section */}
          <Box sx={{ 
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            overflow: 'hidden'
          }}>
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: 400,
                flexDirection: 'column',
                gap: 2
              }}>
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                  Memuat data customer...
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          No
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          minWidth: 200
                        }}>
                          Customer
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Email
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Phone
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Birth Date
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Gender
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Tier
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }}>
                          Date Created
                        </TableCell>
                        <TableCell sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'white', 
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          textAlign: 'center'
                        }}>
                          Aksi
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((customer, index) => (
                        <TableRow 
                          key={customer.id_user}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                              transform: 'scale(1.001)',
                              transition: 'all 0.2s ease'
                            },
                            '&:nth-of-type(even)': {
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                            }
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>
                            {index + 1 + page * rowsPerPage}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar 
                                sx={{ 
                                  width: 40, 
                                  height: 40,
                                  bgcolor: 'primary.main'
                                }}
                              >
                                <PeopleIcon />
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {customer.name || '-'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                  ID: {customer.id_user}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {customer.email || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {customer.phone || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {formatBirthDate(customer.birth_date)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={customer.gender || '-'}
                              variant="outlined"
                              color="info"
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={`Tier ${customer.id_tier || '-'}`}
                              variant="outlined"
                              color="secondary"
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={getStatusLabel(customer.status)}
                              color={getStatusColor(customer.status)}
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {formatDate(customer.date_created)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <IconButton 
                                onClick={() => navigate(`/eureka-plus/external-customers/view/${customer.id_user}`)} 
                                sx={{ 
                                  color: 'primary.main',
                                  '&:hover': { 
                                    bgcolor: 'primary.light',
                                    color: 'white',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                                size="small"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    borderTop: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                  }}
                />
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExternalCustomersPage;




